import React, { forwardRef } from 'react';
import { FlagFilled } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';

import * as Styled from './event.style';
import { EventTime } from './event-time';

export type EventCardProps = {
  title: string;
  // The leading indicator (checkbox / progress circle / icon) including its own
  // slot — kept as an element prop so each card supplies its own indicator while
  // sharing this layout.
  leading: React.ReactNode;
  color?: string | null;
  timeText?: string;
  completed?: boolean;
  isPast?: boolean;
  isDeadline?: boolean;
  numberOfLines: number;
  onClick?: (event: React.MouseEvent) => void;
  onContextMenu?: (event: React.MouseEvent) => void;
};

// Shared layout for the calendar event cards: leading indicator + title + time,
// with the same color/typography/past/completed treatment. Cards differ only in
// the leading element and their data/interaction logic.
export const EventCard = forwardRef<HTMLDivElement, EventCardProps>(
  (
    {
      title,
      leading,
      color,
      timeText,
      completed,
      isPast,
      isDeadline,
      numberOfLines,
      onClick,
      onContextMenu,
    },
    ref,
  ) => (
    <Styled.Container
      ref={ref}
      $color={color}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <Styled.Content>
        {leading}
        <Styled.TextColumn>
          <Styled.Title
            $completed={!!completed}
            // Reserve a line for the time below so it isn't pushed out of a
            // medium-height card.
            $numberOfLines={
              timeText ? Math.max(1, numberOfLines - 1) : numberOfLines
            }
            $color={color}
            $past={isPast}
          >
            {title}
          </Styled.Title>
          {!!timeText && (
            <EventTime
              timeText={timeText}
              completed={completed}
              color={color}
              past={isPast}
            />
          )}
        </Styled.TextColumn>
        {isDeadline && (
          <Styled.DeadlineIconContainer
            $color={color ?? undefined}
            $past={isPast}
          >
            <Icon icon={FlagFilled} />
          </Styled.DeadlineIconContainer>
        )}
      </Styled.Content>
    </Styled.Container>
  ),
);
